type FormatAmountParams = {
  amount: number | null | undefined;
  locale: 'mt-MT';
  currency?: string;
  precision?: number; // number of decimals
};

const LOCALE = 'mt-MT';

/**
 * Helper to create a standardized formatter for currencies and decimals.
 */
const createIntlFormatter = (
  currency: string | null,
  fractionDigits: number,
): Intl.NumberFormat => {
  return new Intl.NumberFormat(LOCALE, {
    style: currency ? 'currency' : 'decimal',
    currency: currency || undefined,
    currencyDisplay: 'code',
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
  });
};

/**
 * For non standardized amounts, we ignore the precision and allow larger
 * decimals in order to show specific values. We also have a fixed
 * position for the currency.
 */
const formatNonStandardizedAmount = ({
  locale,
  amount,
  currency,
}: FormatAmountParams) => {
  const formattedAmount = Intl.NumberFormat(locale, {
    maximumFractionDigits: 18,
  }).format(amount ?? 0);

  if (!currency) {
    return formattedAmount;
  }

  return `${formattedAmount} ${currency}`;
};

// Helper to count decimals
const getPrecision = (num: number) => {
  const str = num?.toString() ?? '';
  return (str.split('.')[1] || '').length;
};

/**
 * Truncates a number to a fixed number of decimals and returns as string.
 * Example: formatMoneyFixedDecimals(69.999525, "EUR", 2) => 'EUR 69.99'
 */
const formatMoneyFixedDecimals = (
  amount: number,
  currency: string | null,
  decimals: number,
): string => {
  const factor = Math.pow(10, decimals);
  const truncated = Math.floor(amount * factor) / factor;

  const isStandardizedCurrency = !currency?.includes(':');

  if (currency && isStandardizedCurrency) {
    return createIntlFormatter(currency, decimals).format(truncated);
  }

  // For non-standardized currencies or no currency, use simple format
  const formattedAmount = truncated.toFixed(decimals);

  return currency ? `${formattedAmount} ${currency}` : formattedAmount;
};

export default function formatMoney(
  amount?: number | null,
  currency?: string | null,
  fractionDigits: number | undefined = 2,
) {
  if (typeof amount !== 'number' || !isFinite(amount)) {
    return '';
  }

  const isStandardizedCurrency = !currency?.includes(':');
  const precision = getPrecision(amount);

  if (currency && precision > fractionDigits) {
    return formatMoneyFixedDecimals(amount, currency, fractionDigits);
  }

  if (currency && !isStandardizedCurrency) {
    return formatNonStandardizedAmount({
      locale: LOCALE,
      currency: currency.split(':')[1],
      amount,
    });
  }

  return createIntlFormatter(currency || null, fractionDigits).format(amount);
}
